import type { TCoursesTrack } from '../types';

// Only the fields these helpers actually read, so callers (and tests) can pass
// anything course-shaped without casting a partial object into a full course.
type TNewlyUpdatedCourse = Pick<TCoursesTrack, 'assignedAt' | 'progress'>;

// Compares calendar day in UTC, not local time — @crehana/date's isSameDay
// uses local getDate/getMonth/getFullYear, which disagrees across viewer
// timezones whenever a timestamp lands close to UTC midnight (verified: it
// flips the same comparison depending on the runtime's TZ). Since both
// timestamps come from the backend as UTC ISO strings, comparing their UTC
// day keeps the result identical for every viewer regardless of local TZ.
const isSameUTCDay = (date1: Date, date2: Date): boolean =>
  date1.getUTCFullYear() === date2.getUTCFullYear() &&
  date1.getUTCMonth() === date2.getUTCMonth() &&
  date1.getUTCDate() === date2.getUTCDate();

// A course is considered "newly updated" when its assigned_at falls on the same
// day as content_updated_at (the track's latest edit). Courses assigned in
// earlier, already-superseded edits won't match the current content_updated_at
// value, so only the latest edit's courses are ever counted.
export const isCourseNewlyUpdated = (
  course: TNewlyUpdatedCourse,
  contentUpdatedAt: string | null,
): boolean => {
  if (!contentUpdatedAt || !course.assignedAt) {
    return false;
  }

  return isSameUTCDay(new Date(contentUpdatedAt), new Date(course.assignedAt));
};

// Completed courses are excluded on purpose: a newly assigned course can already
// be at 100% when the user took it outside the track, and the banner copy asks
// the user to complete the new courses to get an updated diploma. Counting a
// finished course there makes that sentence false, and it also keeps this count
// aligned with the "¡Nuevo!" tag, which the card hides once progress is 100.
export const getNewlyUpdatedCourses = <TCourse extends TNewlyUpdatedCourse>(
  listCourses: TCourse[],
  contentUpdatedAt: string | null,
): TCourse[] => {
  if (!contentUpdatedAt) {
    return [];
  }

  return listCourses.filter(
    course =>
      isCourseNewlyUpdated(course, contentUpdatedAt) && course.progress !== 100,
  );
};
